import { useState } from 'react';
import apiService, { ConsultationFormData } from '../services/api';

// 表单数据接口
export interface FormData {
  name: string;
  phone: string;
  email: string;
  wechat: string;
  company: string;
  demand: string;
}

// 表单验证错误接口
export interface FormErrors {
  [key: string]: string;
}

// 提交状态枚举
export enum SubmitStatus {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

// Hook返回值接口
export interface UseConsultFormReturn {
  form: FormData;
  errors: FormErrors;
  submitStatus: SubmitStatus;
  submitMessage: string;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  resetForm: () => void;
}

// 初始表单数据
const initialFormData: FormData = {
  name: '',
  phone: '',
  email: '',
  wechat: '',
  company: '',
  demand: '',
};

export const useConsultForm = (): UseConsultFormReturn => {
  const [form, setForm] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>(SubmitStatus.IDLE);
  const [submitMessage, setSubmitMessage] = useState<string>('');

  // 处理表单输入变化
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    
    // 清除该字段的错误信息
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // 如果是联系方式字段，清除contact错误
    if ((name === 'phone' || name === 'email' || name === 'wechat') && errors.contact) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.contact;
        return newErrors;
      });
    }
  };

  // 表单验证
  const validate = (): FormErrors => {
    const newErrors: FormErrors = {};

    // 验证必填字段
    if (!form.name.trim()) {
      newErrors.name = '请输入您的称呼';
    } else if (form.name.trim().length > 50) {
      newErrors.name = '称呼长度不能超过50个字符';
    }

    if (!form.company.trim()) {
      newErrors.company = '请输入公司名称';
    } else if (form.company.trim().length > 100) {
      newErrors.company = '公司名称长度不能超过100个字符';
    }

    // 验证至少一种联系方式
    if (!form.phone.trim() && !form.email.trim() && !form.wechat.trim()) {
      newErrors.contact = '请至少填写一种联系方式（手机号、邮箱或微信号）';
    }

    // 验证手机号格式
    if (form.phone.trim()) {
      const phoneRegex = /^1[3-9]\d{9}$/;
      if (!phoneRegex.test(form.phone.trim())) {
        newErrors.phone = '请输入正确的手机号格式';
      }
    }

    // 验证邮箱格式
    if (form.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email.trim())) {
        newErrors.email = '请输入正确的邮箱格式';
      } else if (form.email.trim().length > 100) {
        newErrors.email = '邮箱长度不能超过100个字符';
      }
    }

    // 验证微信号长度
    if (form.wechat.trim() && form.wechat.trim().length > 50) {
      newErrors.wechat = '微信号长度不能超过50个字符';
    }

    // 验证业务诉求长度
    if (form.demand.trim() && form.demand.trim().length > 1000) {
      newErrors.demand = '业务诉求长度不能超过1000个字符';
    }

    return newErrors;
  };

  // 处理表单提交
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    // 验证表单
    const validationErrors = validate();
    setErrors(validationErrors);
    
    // 如果有验证错误，不提交
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    // 设置加载状态
    setSubmitStatus(SubmitStatus.LOADING);
    setSubmitMessage('');

    try {
      // 构造API请求数据
      const apiData: ConsultationFormData = {
        name: form.name.trim(),
        companyName: form.company.trim(),
        businessRequirements: form.demand.trim() || undefined,
      };

      // 添加联系方式
      if (form.phone.trim()) apiData.phone = form.phone.trim();
      if (form.email.trim()) apiData.email = form.email.trim();
      if (form.wechat.trim()) apiData.wechat = form.wechat.trim();

      // 提交表单
      const response = await apiService.submitConsultation(apiData);
      
      // 成功处理
      setSubmitStatus(SubmitStatus.SUCCESS);
      setSubmitMessage(response.message || '咨询表单提交成功，我们将尽快与您联系！');
      
      // 重置表单
      setForm(initialFormData);
      
    } catch (error) {
      // 错误处理
      setSubmitStatus(SubmitStatus.ERROR);
      
      if (error instanceof Error) {
        setSubmitMessage(error.message);
      } else {
        setSubmitMessage('提交失败，请稍后重试');
      }
      
      console.error('表单提交失败:', error);
    }
  };

  // 重置表单
  const resetForm = (): void => {
    setForm(initialFormData);
    setErrors({});
    setSubmitStatus(SubmitStatus.IDLE);
    setSubmitMessage('');
  };

  return {
    form,
    errors,
    submitStatus,
    submitMessage,
    handleChange,
    handleSubmit,
    resetForm,
  };
}; 